// Meta-progression: persistent credits, unlocked planes, perk levels, and the catalog.
// All persisted to localStorage so it survives reloads.

export interface PlaneDef {
  id: string;
  name: string;
  blurb: string;
  cost: number;          // credits to unlock (0 = owned by default)
  // Base stats (perks are applied on top at run start)
  speed: number;
  pitchRate: number;
  rollRate: number;
  yawCouple: number;
  maxHp: number;
  fireInterval: number;
  // Cockpit livery
  cowlingColor: number;
  wingColor: number;
}

export interface PerkDef {
  id: string;
  name: string;
  desc: string;
  costPerLevel: number;  // credits per level (scales: level n costs costPerLevel * n)
  maxLevel: number;
}

/** Plane catalog. The Fokker is the free starter; others cost credits. */
export const PLANES: PlaneDef[] = [
  // Coordinated-turn feel: sideways stick yaws the nose meaningfully (yawCouple) while
  // banking for visual feel (rollRate). Roll is dialed DOWN and yaw UP vs. the old
  // roll-dominant tuning so the plane turns instead of just barrel-rolling.
  {
    id: 'fokker', name: 'Fokker Dr.I', blurb: 'A crimson ace’s triplane. Balanced, sturdy.',
    cost: 0,
    speed: 78, pitchRate: 1.6, rollRate: 2.6, yawCouple: 1.0, maxHp: 20, fireInterval: 0.08,
    cowlingColor: 0x8a1e1e, wingColor: 0x7a1818,
  },
  {
    // First unlock priced to land after 2-3 decent runs (25k total score at 0.1 cr/score),
    // not a 40k-score wall — players should taste variety while still learning the game.
    id: 'albatros', name: 'Albatros D.Va', blurb: 'Faster, harder-hitting, but lightly built.',
    cost: 2500,
    speed: 92, pitchRate: 1.7, rollRate: 2.9, yawCouple: 1.1, maxHp: 15, fireInterval: 0.065,
    cowlingColor: 0x6a5a2a, wingColor: 0x4a5536,
  },
  {
    id: 'pfalz', name: 'Pfalz D.IIIa', blurb: 'Armored brawler. Slow to turn, hard to kill.',
    cost: 6000,
    speed: 68, pitchRate: 1.4, rollRate: 2.2, yawCouple: 0.82, maxHp: 30, fireInterval: 0.09,
    cowlingColor: 0x9a9690, wingColor: 0x6a6e74,
  },
  {
    id: 'sopwith', name: 'Sopwith Snipe', blurb: 'Nimble dogfighter. Twitchy, fragile, fast guns.',
    cost: 9500,
    speed: 88, pitchRate: 2.0, rollRate: 3.1, yawCouple: 1.25, maxHp: 16, fireInterval: 0.06,
    cowlingColor: 0x7a6a48, wingColor: 0x9c8f55,
  },
];

/** Perk catalog. Each level adds to the value; cost scales linearly with level. */
export const PERKS: PerkDef[] = [
  { id: 'armor', name: 'Armored Hull', desc: '+3 max HP per level', costPerLevel: 600, maxLevel: 5 },
  { id: 'engine', name: 'Boost Tuning', desc: '+6 speed per level', costPerLevel: 700, maxLevel: 5 },
  { id: 'guns', name: 'Quick Reload', desc: 'Faster auto-fire per level', costPerLevel: 800, maxLevel: 5 },
  { id: 'medic', name: 'Combat Medic', desc: 'Faster HP regen per level', costPerLevel: 700, maxLevel: 3 },
  { id: 'agile', name: 'Agile Wing', desc: '+8% turn rates per level', costPerLevel: 900, maxLevel: 4 },
];

export interface Progression {
  credits: number;
  ownedPlanes: string[];
  selectedPlane: string;
  perkLevels: Record<string, number>;
  /** Furthest wave ever reached across all runs (0 = never played). */
  bestWave: number;
}

const KEY = 'ww1-progression';

const DEFAULT: Progression = {
  credits: 0,
  ownedPlanes: ['fokker'],
  selectedPlane: 'fokker',
  perkLevels: {},
  bestWave: 0,
};

export function loadProgression(): Progression {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT, ownedPlanes: ['fokker'], perkLevels: {} };
    const p = JSON.parse(raw) as Progression;
    // Defensive merge so older saves don't break.
    return {
      credits: p.credits ?? 0,
      ownedPlanes: Array.isArray(p.ownedPlanes) && p.ownedPlanes.length ? p.ownedPlanes : ['fokker'],
      selectedPlane: p.selectedPlane ?? 'fokker',
      perkLevels: p.perkLevels ?? {},
      bestWave: p.bestWave ?? 0,
    };
  } catch {
    return { ...DEFAULT, ownedPlanes: ['fokker'], perkLevels: {} };
  }
}

export function saveProgression(p: Progression): void {
  try { localStorage.setItem(KEY, JSON.stringify(p)); } catch { /* ignore */ }
}

export function planeById(id: string): PlaneDef {
  return PLANES.find((p) => p.id === id) ?? PLANES[0];
}

/** Compute the effective stats for the selected plane with perks applied. */
export function effectiveStats(prog: Progression) {
  const base = planeById(prog.selectedPlane);
  const lvl = (id: string) => prog.perkLevels[id] ?? 0;
  const turnMul = 1 + 0.08 * lvl('agile');
  return {
    speed: base.speed + 6 * lvl('engine'),
    pitchRate: base.pitchRate * turnMul,
    rollRate: base.rollRate * turnMul,
    yawCouple: base.yawCouple * turnMul,
    maxHp: base.maxHp + 3 * lvl('armor'),
    fireInterval: Math.max(0.04, base.fireInterval - 0.008 * lvl('guns')),
    regenRate: 1 + 0.6 * lvl('medic'), // HP/s
    cowlingColor: base.cowlingColor,
    wingColor: base.wingColor,
  };
}

export type EffectiveStats = ReturnType<typeof effectiveStats>;
